import instance from '../axios-instance';

export type InitiatePaymentResponse = {
  transaction_url: string;
  payment_id: number;
  access_token?: string | null;
  payment_hold_expires_at?: string | null;
};

export type EmbeddedPaymentSessionResponse = {
  session_id: string;
  local_payment_id: number;
  session_expires_at?: string | null;
  payment_hold_expires_at?: string | null;
  sdk_url: string;
  currency: string;
  amount: number;
  access_token?: string | null;
  gateway_method?: 'card' | 'applepay' | 'googlepay';
  supported_networks?: string[] | null;
  provider_callback_url?: string;
};

export type MyFatoorahEmbeddedCallback = {
  isSuccess?: boolean;
  paymentCompleted?: boolean;
  paymentId?: string;
  sessionId?: string;
  paymentData?: string;
  paymentType?: string;
  redirectionUrl?: string;
};

export type MyFatoorahEmbeddedEvent = {
  type?: string;
  name?: string;
};

export type EmbeddedPaymentCompletionResponse = {
  status: string;
  paid_at?: string | null;
  local_payment_id: number;
  redirect?: Record<string, string>;
};

export type CheckoutPaymentOptionId = 'card' | 'mada' | 'applepay' | 'googlepay';

export type PaymentMethod = CheckoutPaymentOptionId;

export type PaymentMethodLogo =
  | 'visa'
  | 'mastercard'
  | 'mada'
  | 'applepay'
  | 'googlepay';

export type CheckoutPaymentOption = {
  id: CheckoutPaymentOptionId;
  gateway_method: 'card' | 'applepay' | 'googlepay';
  label: string;
  logos: PaymentMethodLogo[];
  supported_networks?: string[];
};

/** @deprecated Use CheckoutPaymentOption */
export type PaymentMethodOption = CheckoutPaymentOption;

const CHECKOUT_OPTION_IDS: CheckoutPaymentOptionId[] = [
  'card',
  'mada',
  'applepay',
  'googlepay',
];

const DEFAULT_CHECKOUT_OPTION_IDS: CheckoutPaymentOptionId[] = [
  'card',
  'applepay',
  'googlepay',
];

const DEFAULT_LOGOS_BY_METHOD: Record<CheckoutPaymentOptionId, PaymentMethodLogo[]> = {
  card: ['visa', 'mastercard'],
  mada: ['mada'],
  applepay: ['applepay'],
  googlepay: ['googlepay'],
};

const DEFAULT_GATEWAY_BY_OPTION: Record<
  CheckoutPaymentOptionId,
  CheckoutPaymentOption['gateway_method']
> = {
  card: 'card',
  mada: 'card',
  applepay: 'applepay',
  googlepay: 'googlepay',
};

function isCheckoutOptionId(id: string): id is CheckoutPaymentOptionId {
  return CHECKOUT_OPTION_IDS.includes(id as CheckoutPaymentOptionId);
}

export function normalizeCheckoutPaymentMethods(
  methods?: CheckoutPaymentOption[] | PaymentMethod[] | null
): CheckoutPaymentOption[] {
  if (!methods?.length) {
    return DEFAULT_CHECKOUT_OPTION_IDS.map((id) => ({
      id,
      gateway_method: DEFAULT_GATEWAY_BY_OPTION[id],
      label: id,
      logos: DEFAULT_LOGOS_BY_METHOD[id],
    }));
  }

  const first = methods[0];

  if (
    typeof first === 'object' &&
    first !== null &&
    'id' in first &&
    'label' in first
  ) {
    return (methods as CheckoutPaymentOption[])
      .filter((method) => isCheckoutOptionId(method.id))
      .map((method) => ({
        id: method.id,
        gateway_method:
          method.gateway_method ?? DEFAULT_GATEWAY_BY_OPTION[method.id],
        label: method.label,
        logos: method.logos?.length
          ? method.logos
          : DEFAULT_LOGOS_BY_METHOD[method.id],
        supported_networks: method.supported_networks,
      }));
  }

  return (methods as PaymentMethod[])
    .filter(isCheckoutOptionId)
    .map((id) => ({
      id,
      gateway_method: DEFAULT_GATEWAY_BY_OPTION[id],
      label: id,
      logos: DEFAULT_LOGOS_BY_METHOD[id],
    }));
}

export type CheckoutConfig = {
  available: true;
  mode: 'test' | 'live';
  currency: string;
  default_currency?: string;
  payment_methods?: CheckoutPaymentOption[] | PaymentMethod[];
  provider_callback_url?: string;
  branding: {
    name: string;
    logo: string | null;
    color: string | null;
  };
};

export type PreloadedEmbeddedPaymentSetup = {
  config: CheckoutConfig;
  session: EmbeddedPaymentSessionResponse;
  selectedPaymentMethod: PaymentMethod;
};

export type PaymentStatusResponse = {
  status: string;
  paid_at: string | null;
};

export type AppointmentDetails = {
  id: number;
  appointment_nr: string;
  ics_file_path: string | null;
  date: string;
  time_from: string;
  time_to: string;
  duration: number;
  price: number;
  currency?: string;
  currency_label?: string;
  status: string;
  status_value: string;
  hangout_link: string | null;
  title?: string | null;
  description?: string | null;
  host?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  served_at?: string | null;
  served_at_value?: number | null;
  served_at_data?: string | null;
  visible_to_value?: number | null;
  hide_duration?: boolean;
  rescheduled?: boolean;
  event_type_value?: number | null;
  time_zone?: { name?: string } | null;
  has_e_payment?: boolean;
};

function appointmentAccessConfig(accessToken?: string | null) {
  if (!accessToken) {
    return {};
  }

  return {
    params: { access_token: accessToken },
    headers: { 'X-Appointment-Access-Token': accessToken },
  };
}

export async function getCheckoutConfig(): Promise<CheckoutConfig> {
  const response = await instance.get('payment/config');

  return response.data as CheckoutConfig;
}

export async function getAppointmentPaymentCapabilities(
  appointmentNr: string,
  accessToken?: string | null
): Promise<CheckoutConfig> {
  const response = await instance.get(
    `appointments/${appointmentNr}/payment/capabilities`,
    appointmentAccessConfig(accessToken)
  );

  return response.data as CheckoutConfig;
}

export async function createEmbeddedPaymentSession(
  appointmentNr: string,
  accessToken?: string | null,
  paymentMethod: PaymentMethod = 'card'
): Promise<EmbeddedPaymentSessionResponse> {
  const response = await instance.post(
    `appointments/${appointmentNr}/payment/session`,
    {
      source_type: paymentMethod,
      payment_method: paymentMethod,
    },
    appointmentAccessConfig(accessToken)
  );

  return response.data as EmbeddedPaymentSessionResponse;
}

export async function completeEmbeddedPayment(
  appointmentNr: string,
  payload: {
    local_payment_id: number;
    session_id: string;
    payment_data?: string;
    payment_id?: string;
    payment_type?: string;
  },
  accessToken?: string | null
): Promise<EmbeddedPaymentCompletionResponse> {
  const response = await instance.post(
    `appointments/${appointmentNr}/payment/embedded-result`,
    payload,
    appointmentAccessConfig(accessToken)
  );

  return response.data as EmbeddedPaymentCompletionResponse;
}

export async function getPaymentStatus(
  paymentId: number | string,
  accessToken?: string | null,
  tenantSlug?: string | null
): Promise<PaymentStatusResponse> {
  const tenantPrefix = tenantSlug ? `/${tenantSlug}` : '';
  const response = await instance.get(
    `${tenantPrefix}/payment/myfatoorah/status/${paymentId}`,
    appointmentAccessConfig(accessToken)
  );

  if (
    typeof response.data !== 'object' ||
    response.data === null ||
    typeof response.data.status !== 'string'
  ) {
    throw new Error('Invalid payment status response.');
  }

  return response.data as PaymentStatusResponse;
}

export async function getAppointmentDetails(
  appointmentId: number | string,
  accessToken?: string | null
): Promise<AppointmentDetails> {
  const response = await instance.get(
    `appointments/${appointmentId}`,
    appointmentAccessConfig(accessToken)
  );

  return (response.data?.data ?? response.data) as AppointmentDetails;
}

const MYFATOORAH_HOSTS = [
  'myfatoorah.com',
  'demo.myfatoorah.com',
  'apitest.myfatoorah.com',
  'api.myfatoorah.com',
  'sa.myfatoorah.com',
  'portal.myfatoorah.com',
  'eg.myfatoorah.com',
  'kw.myfatoorah.com',
];

export function callbackOriginsFromUrls(
  ...urls: Array<string | null | undefined>
): string[] {
  const origins = new Set<string>();

  for (const url of urls) {
    if (!url) {
      continue;
    }

    try {
      origins.add(new URL(url).origin.toLowerCase());
    } catch {
      continue;
    }
  }

  return Array.from(origins);
}

export function isSafeRedirectUrl(url: string): boolean {
  try {
    const parsed = new URL(url);

    if (import.meta.env.PROD) {
      return parsed.protocol === 'https:';
    }

    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

export function isSafeMyFatoorahRedirectUrl(
  url: string,
  allowedCallbackOrigins: string[] = []
): boolean {
  if (!isSafeRedirectUrl(url)) {
    return false;
  }

  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();

    const isMyFatoorahHost =
      hostname === 'myfatoorah.com' ||
      hostname.endsWith('.myfatoorah.com') ||
      MYFATOORAH_HOSTS.some(
        (host) => hostname === host || hostname.endsWith(`.${host}`)
      );

    if (isMyFatoorahHost) {
      return parsed.protocol === 'https:';
    }

    const origin = parsed.origin.toLowerCase();

    return allowedCallbackOrigins.some(
      (allowedOrigin) => allowedOrigin.toLowerCase() === origin
    );
  } catch {
    return false;
  }
}

/** Embedded card/wallet completion — verify server-side instead of following redirectionUrl. */
export function hasEmbeddedCompletionPayload(
  callback: MyFatoorahEmbeddedCallback
): boolean {
  const paymentId = callback.paymentId?.trim();
  const paymentData = callback.paymentData?.trim();

  return Boolean(paymentId || paymentData);
}
